import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import _ from 'lodash';
import { forkJoin, Observable, ReplaySubject, Subscription, BehaviorSubject } from 'rxjs';
import { InviteUserDialogLegacyComponent } from 'src/app/components/invite-user-dialog-legacy/invite-user-dialog-legacy.component';
import { CheckboxClicked, DeleteClicked, OptionSelected } from 'src/app/components/table-row/table-row.component';
import { WarningDialogComponent } from 'src/app/components/warning-dialog/warning-dialog.component';
import {
  AccountManagementAuthenticationModel,
  AccountManagementInvitationModel,
  AccountManagementUserModel,
  ModifyUserRoleModel,
  UsersSupportedActions,
  ZenityUserRoleEnum,
} from 'src/app/models/api/accountManagement.model';
import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { MenuAction } from 'src/app/models/menuAction.model';
import { RoleSelectOptions } from 'src/app/models/selectOptions/roleSelectOptions.model';
import { UsersLegacyTableColumns } from 'src/app/models/tableColumns/usersLegacyColumns.model';
import { UserViewModel } from 'src/app/models/viewModels/userViewModel';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { AccountManagementHttpService } from 'src/app/services/httpServices/accountManagementHttpService/account-management-http-service.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { UserService } from 'src/app/services/infra/user/user.service';

@Component({
  selector: 'app-users-legacy',
  templateUrl: './users-legacy.component.html',
  styleUrls: ['./users-legacy.component.scss'],
})
export class UsersLegacyComponent implements OnInit, OnDestroy {
  public users: AccountManagementUserModel[] = [];
  public invitations: AccountManagementInvitationModel[] = [];
  public authentications: AccountManagementAuthenticationModel[] = [];

  public usersCurrentPage$: BehaviorSubject<BehaviorSubject<UserViewModel>[]> = new BehaviorSubject<
    BehaviorSubject<UserViewModel>[]
  >([]);

  public resetSearchBoxBehavioralSubject$: ReplaySubject<undefined> = new ReplaySubject<undefined>();

  public tableRows: Observable<BehaviorSubject<UserViewModel>[]>;
  public usersTableCols = UsersLegacyTableColumns;
  public roleSelectOptions = RoleSelectOptions;

  // form control - checkbox
  public allRowsSelected = new FormControl(false);
  public checkedUserIds: Set<string> = new Set();
  public checkedUserNames: Set<string> = new Set();

  // bulk action menu
  public multiSelectedMenuActions$: BehaviorSubject<MenuAction[]> = new BehaviorSubject<MenuAction[]>([]);

  // loading behavior subject
  public isLoading = signal<boolean>(true);

  private _searchedString?: string = '';

  // Subscriptions
  private _changeUserRoleSubscription$?: Subscription;

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _accountHttpService: AccountManagementHttpService,
    private readonly _permissionsService: PermissionsService,
    private readonly _notificationService: NotificationsService,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _userService: UserService
  ) {
    // in order to keep the scope of the function for this. inspiration: https://stackoverflow.com/questions/38245450/angular2-components-this-is-undefined-when-executing-callback-function
    this.deleteUser = this.deleteUser.bind(this);
    this.deleteInvitation = this.deleteInvitation.bind(this);
    this.handleMultipleDeleteAction = this.handleMultipleDeleteAction.bind(this);
  }

  ngOnInit(): void {
    this.isLoading.set(true);
    this._requestForPageData();
  }

  ngOnDestroy(): void {
    this._changeUserRoleSubscription$?.unsubscribe();
  }

  public userSearched(searchedUsers?: string) {
    this._searchedString = searchedUsers;
    this._buildUsersViewModel(searchedUsers);
  }

  public inviteUserClicked() {
    const dialogRef = this._matDialog.open(InviteUserDialogLegacyComponent, {
      data: { authentications: this.authentications },
      disableClose: false,
      panelClass: 'dynamic-size-popup-dialog-container',
    });
    dialogRef.afterClosed().subscribe((userInvited?: boolean) => {
      if (userInvited) {
        this._refreshTable();
      }
    });
  }

  public onCheckboxClick(checkbox: CheckboxClicked) {
    const id: string = checkbox.id;
    const status: boolean = checkbox.status;
    const userRow = checkbox.row.value as UserViewModel;

    if (status) {
      this.checkedUserIds.add(id);
      this.checkedUserNames.add(userRow.email);
    } else {
      this.checkedUserIds.delete(id);
      this.checkedUserNames.delete(userRow.email);
      this.allRowsSelected.setValue(false, { emitEvent: false });
    }

    if (this.checkedUserIds.size == this._accountHttpService.defaultPageSize) {
      this.allRowsSelected.setValue(true, { emitEvent: false });
    }
  }

  public deleteUser(ids: string[]): Observable<string> {
    return this._accountHttpService.deleteUsers(ids);
  }

  public deleteInvitation(ids: string[]): Observable<string> {
    return this._accountHttpService.deleteInvitation(ids[0]);
  }

  public deleteIconOnClick(userDetails: DeleteClicked) {
    const userRow = userDetails.moreData as UserViewModel;
    const isUser = userRow.isUser;
    const elementName = isUser ? userRow.name : userRow.email;

    const dialogRef = this._matDialog.open(WarningDialogComponent, {
      data: {
        elementsIds: [userDetails.id],
        elementsNames: [elementName],
        title: 'userManagementPage.usersPage.deleteUserTitle',
        actionCallback: isUser ? this.deleteUser : this.deleteInvitation,
        notificationMsgLabelPath: 'common.wasDeletedSuccessfully',
      },
      disableClose: false,
      panelClass: 'sized-popup-dialog-container',
    });
    dialogRef.afterClosed().subscribe((status) => {
      if (status) {
        this._refreshTable();
      }
    });
  }

  public multiSelectedActionClick(menuAction: MenuAction) {
    if (menuAction.name === UsersSupportedActions.Delete.toString()) {
      const dialogRef = this._matDialog.open(WarningDialogComponent, {
        data: {
          elementsIds: this.checkedUserIds,
          elementsNames: this.checkedUserNames,
          title: 'userManagementPage.usersPage.deleteUserTitle',
          actionCallback: this.handleMultipleDeleteAction,
          notificationMsgLabelPath: 'common.wasDeletedSuccessfully',
        },
        disableClose: false,
        panelClass: 'sized-popup-dialog-container',
      });
      dialogRef.afterClosed().subscribe((status) => {
        if (status) {
          this._refreshTable();
        }
      });
    }
  }

  public onRoleSelected(optionSelected: OptionSelected) {
    const user = _.find(this.users, { userId: optionSelected.id });
    const dialogRef = this._matDialog.open(WarningDialogComponent, {
      data: {
        elementsIds: [user?.userId],
        elementsNames: [user?.name ? user?.name : user?.email],
        title: 'userManagementPage.usersPage.roleModificationTitle',
        body: 'userManagementPage.usersPage.roleModificationBody',
        notificationMsgLabelPath: 'common.wasDeletedSuccessfully',
      },
      disableClose: false,
      panelClass: 'sized-popup-dialog-container',
    });
    dialogRef.afterClosed().subscribe((status) => {
      if (status) {
        this._assignRoleToUser(optionSelected);
      } else {
        this._buildUsersViewModel(this._searchedString);
      }
    });
  }

  public handleMultipleDeleteAction(usersOrInvitationsIds: Set<string>) {
    let userIdsToDelete: string[] = _.map(this.users, 'userId');
    userIdsToDelete = userIdsToDelete.filter((id) => usersOrInvitationsIds.has(id));
    let invitationIdsToDelete: string[] = _.map(this.invitations, 'id');
    invitationIdsToDelete = invitationIdsToDelete.filter((id) => usersOrInvitationsIds.has(id));

    const observables: Observable<string>[] = [];

    for (const invitation of invitationIdsToDelete) {
      observables.push(this._accountHttpService.deleteInvitation(invitation));
    }
    if (userIdsToDelete.length > 0) {
      observables.push(this._accountHttpService.deleteUsers(userIdsToDelete));
    }

    return forkJoin(observables);
  }

  private _assignRoleToUser(optionSelected: OptionSelected) {
    this.isLoading.set(true);
    const newRole: ZenityUserRoleEnum =
      ZenityUserRoleEnum[optionSelected.option.value.toLowerCase() as ZenityUserRoleEnum];
    const user = _.find(this.users, { userId: optionSelected.id });
    if (user) {
      this._changeUserRoleSubscription$ = this._accountHttpService
        .modifyRole({
          userId: optionSelected.id,
          newRole: newRole,
        } as ModifyUserRoleModel)
        .subscribe(
          (newRoleResponse) => {
            const user = _.find(this.users, {
              userId: optionSelected.id,
            });
            if (user) {
              const newRole: ZenityUserRoleEnum =
                ZenityUserRoleEnum[newRoleResponse.toLowerCase() as ZenityUserRoleEnum];
              user.role = newRole;

              this._notificationService.success('Role assignment succeeded');
              this._buildUsersViewModel();
              this.isLoading.set(false);
            }
          },
          (error) => {
            if (error.status == 422) {
              this._notificationService.warning('User ' + user?.email + ' has already ' + newRole + ' role');
            }
            this._buildUsersViewModel();
            this.isLoading.set(false);
          }
        );
    }
  }

  private _refreshTable() {
    this.isLoading.set(true);
    this.checkedUserIds.clear();
    this.checkedUserNames.clear();
    this._requestForPageData();
  }

  private _buildUsersViewModel(searched?: string) {
    const viewModels: BehaviorSubject<UserViewModel>[] = [];

    searched = searched?.toLowerCase();

    this._buildUserViewModelForInvitations(viewModels, searched);

    this._buildUserViewModelForUsers(viewModels, searched);

    this.usersCurrentPage$.next(viewModels);

    this.tableRows = new BehaviorSubject(viewModels);
  }

  private _buildUserViewModelForInvitations(viewModels: BehaviorSubject<UserViewModel>[], searched?: string) {
    this.invitations.forEach((invitation) => {
      if (!searched || invitation.email.toLowerCase().includes(searched)) {
        const roleSelectOption = _.find(this.roleSelectOptions, {
          id: `${FilterTypesEnum.Role.toString()}-${invitation.role}`,
        });
        const viewModel: UserViewModel = {
          rowId: invitation.id,
          email: invitation.email,
          name: '',
          createdAt: invitation.createdAt,
          // It is here because that path is not URL, it raised an error in the console
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          pictureUrl: 'assets/images/common/pending-icon.svg' as any,
          role: roleSelectOption,
          disabled: true,
          clickable: false,
          isUser: false,
          invitationDesc: 'Invitation pending',
        } as UserViewModel;

        viewModels.push(new BehaviorSubject(viewModel));
      }
    });
  }

  private _buildUserViewModelForUsers(viewModels: BehaviorSubject<UserViewModel>[], searched?: string) {
    this.users.forEach((user) => {
      if (!searched || user.email.toLowerCase().includes(searched) || user.name.toLowerCase().includes(searched)) {
        const roleSelectOption = _.find(this.roleSelectOptions, {
          id: `${FilterTypesEnum.Role.toString()}-${user.role}`,
        });
        const viewModel: UserViewModel = {
          rowId: user.userId,
          email: user.email,
          name: user.name,
          createdAt: user.createdAt,
          lastLogin: user.lastLogin?.toString() ? new Date(user.lastLogin) : undefined,
          pictureUrl: user.pictureUrl,
          role: roleSelectOption,
          clickable: false,
          isUser: true,
          isStrictMode: this._userService.loggedInUser?.email === user.email,
        } as UserViewModel;

        viewModels.push(new BehaviorSubject(viewModel));
      }
    });
  }

  private _onAllRowsSelectionClick(newValue: boolean | null) {
    const checkboxStatus = newValue ? true : false;

    this.usersCurrentPage$.value.forEach((userRow$) => {
      const userRow = userRow$.value;
      userRow.checkboxStatus = checkboxStatus;
      userRow$.next(userRow);
      if (checkboxStatus === true) {
        this.checkedUserIds.add(userRow.rowId);
        this.checkedUserNames.add(userRow.email);
      } else {
        this.checkedUserIds.delete(userRow.rowId);
        this.checkedUserNames.delete(userRow.email);
      }
    });
  }

  private _requestForPageData() {
    forkJoin([
      this._accountHttpService.getUsers(),
      this._accountHttpService.getInvitations(),
      this._accountHttpService.getAuthentications(),
    ]).subscribe((observablesResults) => {
      this.users = observablesResults[0];
      this.invitations = observablesResults[1];
      this.authentications = observablesResults[2];

      this.allRowsSelected.valueChanges.subscribe((value) => this._onAllRowsSelectionClick(value));
      this._updateMultiSelectActions();

      this.isLoading.set(false);
      this._buildUsersViewModel();
    });
  }

  private _updateMultiSelectActions() {
    this.multiSelectedMenuActions$.next([
      {
        name: UsersSupportedActions.Delete.toString(),
        displayNameLabel: 'userManagementPage.usersPage.deleteUsersMultiActionName',
        iconPath: 'assets/images/common/delete.svg',
        disabled: !this._permissionsService.isAdmin(),
      } as MenuAction,
    ]);
  }
}
